import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  caregiverVerificationService,
  QueueCaregiver,
  VerificationDecisionRow,
} from '../services/caregiverVerificationService';
import VerificationReviewSidebar from '../components/VerificationReviewSidebar';

// FEAT-039 — Admin Caregiver Verification Queue page (MVP-8.1, MVP-18.14).
// Lists caregivers awaiting verification, their uploaded credential
// documents, and exposes Verify / Reject controls via the side drawer.
// Every decision is audit-logged server-side.

const CaregiverVerificationQueuePage: React.FC = () => {
  const [rows, setRows] = useState<QueueCaregiver[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<QueueCaregiver | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await caregiverVerificationService.listQueue({
        page: page + 1,
        limit: rowsPerPage,
      });
      setRows(result.caregivers);
      setTotal(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load queue');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRowClick = (row: QueueCaregiver) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const handleDecided = (
    caregiverId: string,
    _decision: VerificationDecisionRow,
  ) => {
    setRows((prev) => prev.filter((r) => r.id !== caregiverId));
    setTotal((prev) => Math.max(0, prev - 1));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Caregiver Verification Queue
        </Typography>
        <IconButton onClick={fetchData} aria-label="Refresh queue">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review uploaded credential documents and mark caregivers Verified or
        Rejected. Decisions are audit-logged and trigger a badge recompute.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="verification-queue-error">
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table aria-label="Caregiver verification queue">
            <TableHead>
              <TableRow>
                <TableCell>Caregiver</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>State</TableCell>
                <TableCell>License</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No caregivers are awaiting verification right now.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const fullName =
                    [row.firstName, row.lastName].filter(Boolean).join(' ') ||
                    'Caregiver';
                  const docCount = row.credentialDocuments.length;
                  const priorRejection = row.latestDecision?.decision === 'REJECTED';
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{ cursor: 'pointer' }}
                      data-testid={`verification-queue-row-${row.id}`}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{fullName}</Typography>
                          {priorRejection && (
                            <Chip label="Re-submitted" size="small" color="warning" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.stateCode ?? '—'}</TableCell>
                      <TableCell>{row.stateLicenseNumber ?? '—'}</TableCell>
                      <TableCell>{docCount}</TableCell>
                      <TableCell>
                        <Chip
                          label={priorRejection ? 'Re-review' : 'Pending'}
                          size="small"
                          color={priorRejection ? 'warning' : 'info'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>

      <VerificationReviewSidebar
        caregiver={selected}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDecided={handleDecided}
      />
    </Container>
  );
};

export default CaregiverVerificationQueuePage;
